import React, { useRef, useState } from 'react'
import Link from 'components/elements/Link'
import { useRouter } from 'next/router'
import { Button, Checkbox, Icon } from 'semantic-ui-react'

import { defaultDiffableFields, DiffableFields } from 'components/comparePage/utils'

import CollapsibleContentContainer from 'components/elements/CollapsibleContentContainer'
import LineLoader from 'components/elements/LineLoader'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'

import { createQueryParams } from 'utils/queryParams/generic.queryParams'

import styles from './diffControls.module.scss'

const DiffControls = () => {
  const ref = useRef<HTMLButtonElement>(null)

  const router = useRouter()
  const { diffFields } = router.query

  const enabledDiffFields = (
    typeof diffFields === 'string' ? diffFields?.split(',') : defaultDiffableFields
  ) as DiffableFields[]

  const [ignoreSideboard, setIgnoreSideboard] = useState(!!router.query.ignoreSideboard)
  const [includeMaybeboard, setIncludeMaybeboard] = useState(!!router.query.includeMaybeboards)
  const [limtedCardType, setLimtedCardType] = useState(`${router.query.limitedCardType || ''}` || undefined)

  const [diffQuantity, setDiffQuantity] = useState(enabledDiffFields.includes('totalQuantity'))
  const [diffEditions, setDiffEditions] = useState(enabledDiffFields.includes('totalEditions'))
  const [diffFinishes, setDiffFinishes] = useState(enabledDiffFields.includes('totalFinishes'))
  const [diffCustomCmc, setDiffCustomCmc] = useState(enabledDiffFields.includes('totalCustomCmc'))
  const [diffCompanion, setDiffCompanion] = useState(enabledDiffFields.includes('totalCompanion'))
  const [diffColorLabel, setDiffColorLabel] = useState(enabledDiffFields.includes('totalColorLabel'))
  const [diffCategories, setDiffCategories] = useState(enabledDiffFields.includes('totalCategories'))

  const [loading, setLoading] = useState(false)

  const handleApplyAndReload = () => {
    ref?.current?.focus({})

    const newDiffFields = new Array<DiffableFields>()

    if (diffQuantity) newDiffFields.push('totalQuantity')
    if (diffEditions) newDiffFields.push('totalEditions')
    if (diffFinishes) newDiffFields.push('totalFinishes')
    if (diffCustomCmc) newDiffFields.push('totalCustomCmc')
    if (diffCompanion) newDiffFields.push('totalCompanion')
    if (diffColorLabel) newDiffFields.push('totalColorLabel')
    if (diffCategories) newDiffFields.push('totalCategories')

    handleUpdatePrams({ ...router.query, diffFields: newDiffFields.join(',') })
  }

  const handleToggleIgnoreSideboard = () => {
    setIgnoreSideboard(!ignoreSideboard)

    const updatedParams = { ...router.query } as Record<string, string>

    if (!ignoreSideboard) updatedParams.ignoreSideboard = 'true'
    else delete updatedParams.ignoreSideboard

    handleUpdatePrams(updatedParams)
  }

  const handleToggleIncludeMaybeboard = () => {
    setIncludeMaybeboard(!includeMaybeboard)

    const updatedParams = { ...router.query } as Record<string, string>

    if (!includeMaybeboard) updatedParams.includeMaybeboards = 'true'
    else delete updatedParams.includeMaybeboards

    handleUpdatePrams(updatedParams)
  }

  const handleUpdateLimtedCardType = (cardType?: string) => {
    setLimtedCardType(cardType)

    const updatedParams = { ...router.query } as Record<string, string>

    if (cardType) updatedParams.limitedCardType = cardType
    else delete updatedParams.limitedCardType

    handleUpdatePrams(updatedParams)
  }

  const handleUpdatePrams = (updatedParams: Record<string, string>) => {
    setLoading(true)

    setTimeout(() => {
      setLoading(false)

      router.push({ pathname: `${router.pathname}`, query: updatedParams }, undefined, { shallow: true })
    }, 1000)
  }

  const swapUrl = `/compare/${createQueryParams({ ...router.query, one: router.query.two, two: router.query.one })}`

  return (
    <>
      <CollapsibleContentContainer name="Diff Controls">
        <div className={styles.controls}>
          <Link className={styles.swapDecks} href={swapUrl}>
            <Button>
              A <Icon name="exchange" /> B
            </Button>
          </Link>

          <ArchidektDropdown
            menuClassName={styles.dropdownMenu}
            options={[
              {
                label: 'Check all',
                onClick: () => {
                  setDiffQuantity(true)
                  setDiffEditions(true)
                  setDiffFinishes(true)
                  setDiffCustomCmc(true)
                  setDiffCompanion(true)
                  setDiffColorLabel(true)
                  setDiffCategories(true)
                },
                icon: 'check circle',
                noCloseOnClick: true,
              },
              {
                label: 'Uncheck all',
                onClick: () => {
                  setDiffQuantity(false)
                  setDiffEditions(false)
                  setDiffFinishes(false)
                  setDiffCustomCmc(false)
                  setDiffCompanion(false)
                  setDiffColorLabel(false)
                  setDiffCategories(false)
                },
                icon: 'circle outline',
                noCloseOnClick: true,
              },
              { type: 'spacer' },
              {
                type: 'checkbox',
                checked: diffQuantity,
                label: 'Quantity',
                onChange: () => setDiffQuantity(!diffQuantity),
              },
              {
                type: 'checkbox',
                checked: diffEditions,
                label: 'Printings',
                onChange: () => setDiffEditions(!diffEditions),
              },
              {
                type: 'checkbox',
                checked: diffFinishes,
                label: 'Finishes',
                onChange: () => setDiffFinishes(!diffFinishes),
              },
              {
                type: 'checkbox',
                checked: diffCustomCmc,
                label: 'Custom Cmc',
                onChange: () => setDiffCustomCmc(!diffCustomCmc),
              },
              {
                type: 'checkbox',
                checked: diffCompanion,
                label: 'Companion',
                onChange: () => setDiffCompanion(!diffCompanion),
              },
              {
                type: 'checkbox',
                checked: diffColorLabel,
                label: 'Color Label',
                onChange: () => setDiffColorLabel(!diffColorLabel),
              },
              {
                type: 'checkbox',
                checked: diffCategories,
                label: 'Categories',
                onChange: () => setDiffCategories(!diffCategories),
              },
              { type: 'spacer' },
              {
                type: 'custom',
                className: styles.applyContainer,
                customChild: (
                  <button onClick={handleApplyAndReload} className={styles.applyButton}>
                    Apply and Reload
                  </button>
                ),
              },
            ]}>
            <Button onClick={() => null}>
              <Icon name="filter" /> Choose Compare Filters
            </Button>
          </ArchidektDropdown>
        </div>

        <div className={styles.controls}>
          <ArchidektDropdown
            menuClassName={styles.typesMenu}
            options={[
              { label: 'All', onClick: () => handleUpdateLimtedCardType(undefined) },
              { label: 'Artifact', onClick: () => handleUpdateLimtedCardType('Artifact') },
              { label: 'Creature', onClick: () => handleUpdateLimtedCardType('Creature') },
              { label: 'Enchantment', onClick: () => handleUpdateLimtedCardType('Enchantment') },
              { label: 'Instant', onClick: () => handleUpdateLimtedCardType('Instant') },
              { label: 'Land', onClick: () => handleUpdateLimtedCardType('Land') },
              { label: 'Planeswalker', onClick: () => handleUpdateLimtedCardType('Planeswalker') },
              { label: 'Sorcery', onClick: () => handleUpdateLimtedCardType('Sorcery') },
            ]}>
            <Button>Card types: {limtedCardType ?? 'All'}</Button>
          </ArchidektDropdown>

          <span className={`${styles.controls} ${styles.boardInclusions}`}>
            <Checkbox checked={ignoreSideboard} onChange={handleToggleIgnoreSideboard} label="Ignore Sideboards" />
            <Checkbox
              checked={includeMaybeboard}
              onChange={handleToggleIncludeMaybeboard}
              label="Include Maybeboards"
            />
          </span>
        </div>
      </CollapsibleContentContainer>
      {loading ? <LineLoader /> : <span className={styles.spacer} />}
      {/* Used to unfocus the dropdown on option selection */}
      <button tabIndex={-1} ref={ref} style={{ position: 'absolute', transform: 'scale(0)', height: 0, width: 0 }} />
    </>
  )
}

export default DiffControls
